import React, { useState } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { validateFunc } from '../../constraints/constraints'
import { updateOrderStatus, getConfiguration } from '../../apollo'
import BrandLoader from '../Loader/BrandLoader'
import { Box, Alert, InputBase, Button } from '@mui/material'
import useGlobalStyles from '../../utils/globalStyles'
import OrderDetailView, { BRAND, BRAND_DARK } from './OrderDetailView'
import AcceptOrderDialog from './AcceptOrderDialog'
import OrderRefunds, { canRecordRefunds } from './OrderRefunds'

// constants
const UPDATE_STATUS = gql`
  ${updateOrderStatus}
`

const GET_CONFIGURATION = gql`
  ${getConfiguration}
`

function Order(props) {
  const { order, t } = props
  const [reason, reasonSetter] = useState('')
  const [reasonError, reasonErrorSetter] = useState(false)
  const [error, errorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [accepting, setAccepting] = useState(false)

  const onCompleted = ({ updateOrderStatus }) => {
    setAccepting(false)
    if (updateOrderStatus) {
      successSetter(t('OrderStatusUpdated'))
    }
    setTimeout(onDismiss, 5000)
  }
  const onError = error => {
    errorSetter(error.message)
    setTimeout(onDismiss, 5000)
  }
  const { data } = useQuery(GET_CONFIGURATION)
  const [mutate, { loading }] = useMutation(UPDATE_STATUS, {
    onError,
    onCompleted
  })

  const cancelReason = reason || (order && order.reason) || ''

  // Returns true when the reason is valid.
  const validateReason = () => {
    const valid = !validateFunc({ reason: cancelReason }, 'reason')
    reasonErrorSetter(!valid)
    return valid
  }

  const onDismiss = () => {
    errorSetter('')
    successSetter('')
  }

  const globalClasses = useGlobalStyles()
  if (!props.order) return null

  const currency = (data && data.configuration.currencySymbol) || ''
  const isOpen =
    order.orderStatus !== 'CANCELLED' && order.orderStatus !== 'DELIVERED'

  return (
    <OrderDetailView
      order={order}
      currency={currency}
      onClose={props.onClose}
      footer={
        <>
          {isOpen && (
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                bgcolor: '#FFFFFF',
                borderTop: `1px solid ${LINE}`,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5
              }}>
              <InputBase
                placeholder={t('PHReasonIfRejected')}
                value={cancelReason}
                onChange={event => {
                  reasonSetter(event.target.value)
                  if (reasonError) reasonErrorSetter(false)
                }}
                sx={{
                  flex: '1 1 240px',
                  height: 42,
                  px: 1.75,
                  fontSize: 14,
                  borderRadius: '10px',
                  border: `1px solid ${reasonError ? '#EF4444' : '#E5E7EB'}`,
                  bgcolor: '#FFFFFF',
                  '&.Mui-focused': {
                    borderColor: reasonError ? '#EF4444' : BRAND
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 1.25, ml: 'auto' }}>
                {loading && <BrandLoader size={32} label={null} />}
                <Button
                  variant="outlined"
                  disabled={loading}
                  onClick={() => {
                    if (validateReason()) {
                      mutate({
                        variables: {
                          id: order._id,
                          status: 'CANCELLED',
                          reason: cancelReason
                        }
                      })
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '999px',
                    px: 2.5,
                    height: 42,
                    color: '#B91C1C',
                    borderColor: '#FCA5A5',
                    '&:hover': { borderColor: '#EF4444', bgcolor: '#FEF2F2' }
                  }}>
                  {t('Cancel')}
                </Button>
                {order.orderStatus === 'PENDING' && (
                  <Button
                    variant="contained"
                    disableElevation
                    disabled={loading}
                    onClick={() => {
                      // Scheduled orders are due in days, not prep minutes
                      if (order.deliveryType === 'scheduled') {
                        mutate({
                          variables: {
                            id: order._id,
                            status: 'ACCEPTED',
                            reason: ''
                          }
                        })
                      } else {
                        setAccepting(true)
                      }
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '999px',
                      px: 3,
                      height: 42,
                      bgcolor: BRAND,
                      '&:hover': { bgcolor: BRAND_DARK }
                    }}>
                    {t('Accept')}
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {canRecordRefunds() && (
            <OrderRefunds orderId={order.orderId} currency={currency} />
          )}

          <AcceptOrderDialog
            open={accepting}
            loading={loading}
            onClose={() => setAccepting(false)}
            onConfirm={time =>
              mutate({
                variables: {
                  id: order._id,
                  status: 'ACCEPTED',
                  reason: '',
                  time
                }
              })
            }
          />

          {(success || error) && (
            <Box sx={{ px: 3, pb: 2, bgcolor: '#FFFFFF' }}>
              {success && (
                <Alert
                  className={globalClasses.alertSuccess}
                  variant="filled"
                  severity="success">
                  {success}
                </Alert>
              )}
              {error && (
                <Alert
                  className={globalClasses.alertError}
                  variant="filled"
                  severity="error">
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </>
      }
    />
  )
}
export default withTranslation()(Order)
